import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Alert,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Link,
  Divider,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  SyncAlt as SyncAltIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  DatasetLinked as DatasetLinkedIcon
} from '@mui/icons-material'

// Import custom hooks
import { useFranchisePartnersData, FranchisePartner } from '../../components/admin/franchise-partners/hooks/useFranchisePartnersData'
import { useFranchisePartnersFilters } from '../../components/admin/franchise-partners/hooks/useFranchisePartnersFilters'
import { useBulkOperations } from '../../components/admin/franchise-partners/hooks/useBulkOperations'
import { useSyncOperations } from '../../components/admin/franchise-partners/hooks/useSyncOperations'

// Import components
import FeedbackNotificationBar from '../../components/feedback/FeedbackNotificationBar'
import ClearDataDialog from '../../components/admin/franchise-partners/dialogs/ClearDataDialog'
import BulkSyncDialog from '../../components/shared/dialogs/BulkSyncDialog'
import ExportConfirmDialog from '../../components/admin/franchise-partners/dialogs/ExportConfirmDialog'
import { ReportFilters } from '../../components/shared/filters/types'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'

// Import services and utils
import { franchisePartnersService } from '../../components/admin/franchise-partners/services/franchisePartnersService'
import { formatters, validators } from '../../components/admin/franchise-partners/utils/franchisePartnersUtils'
import { generateAdminFpUrl } from '../../utils/urls'

// Import other dependencies
import { formatTimeAgo } from '../../utils/tableFormatters'
import { poolwerxColors } from '../../theme/colors'
import { FPStatus, EditableStatusChip } from '../../components/admin/franchise-partners/EditableStatusChip'
import SystemDataChips from '../../components/admin/franchise-partners/SystemDataChips'
import AdoptionIndicator from '../../components/admin/franchise-partners/AdoptionIndicator'
import ReportFilterCard from '../../components/shared/filters/ReportFilterCard'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import { FilterKey } from '../../components/shared/filters/types'
import { LightspeedIcon, XeroIcon, InspectorIcon } from '../../components/SystemIcons'
import { useNotify } from '../../components/shared/ToastProvider'
import { getResourceIcon, resourceTypeSortKey } from '../../utils/resourceIcons'

const ENABLED_FILTERS: FilterKey[] = ['textSearch', 'fpStatus', 'market', 'region', 'romFbc', 'icuStatus', 'sellingStatus', 'systemStatus', 'adoptionStatus']

const FranchisePartners: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // State management using custom hooks
  const {
    franchisePartners,
    loading,
    error: dataError,
    refetch,
    updatePartner
  } = useFranchisePartnersData()

  const {
    filters,
    updateFilter,
    clearFilters,
    sortField,
    sortDirection,
    handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    filteredData,
    paginatedData,
    totalPages,
    hasActiveFilters,
    getActiveFilterLabels
  } = useFranchisePartnersFilters(franchisePartners)

  const {
    selectedFranchises,
    handleSelectFranchise,
    handleSelectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate
  } = useBulkOperations()

  // State for messages and dialogs
  const { notify, showSuccess, showError } = useNotify()
  // Sync-trigger acks deep-link to the active-syncs monitor.
  const notifySyncQueued = (message: string) =>
    notify({
      severity: 'success',
      message,
      actionUrl: '/admin/data-sync/active',
      actionLabel: 'View syncs',
    })
  // Sync failures persist (error severity) and keep the same deep-link action
  // so the user can jump straight to the monitor to investigate.
  const notifySyncError = (message: string) =>
    notify({
      severity: 'error',
      message,
      actionUrl: '/admin/data-sync/active',
      actionLabel: 'View syncs',
    })
  const [dataRefreshKey, setDataRefreshKey] = useState(0)

  // Clear data dialog state
  const [clearDataDialog, setClearDataDialog] = useState(false)
  const [clearFp, setClearFp] = useState('')
  const [selectedFranchisePartner, setSelectedFranchisePartner] = useState<any>(null)
  const [isBulkOperation, setIsBulkOperation] = useState(false)

  // Bulk sync dialog state
  const [bulkSyncDialog, setBulkSyncDialog] = useState(false)

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Menu anchors
  const [bulkActionsMenuAnchor, setBulkActionsMenuAnchor] = useState<HTMLElement | null>(null)
  const [syncMenuAnchor, setSyncMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})
  const [quickLinksMenuAnchor, setQuickLinksMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})
  const [linksMenuAnchor, setLinksMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({})

  // Sync operations
  const {
    triggerSync,
    triggerSyncAll,
    syncingFps,
    activeSyncs,
    fetchActiveSyncs
  } = useSyncOperations(
    (message) => {
      notifySyncQueued(message)
    },
    (message) => {
      notifySyncError(message)
    }
  )

  // Initialize
  useEffect(() => {
    fetchActiveSyncs()
  }, [fetchActiveSyncs])

  useEffect(() => {
    if (location.state?.message) {
      showSuccess(location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, navigate, location.pathname])

  // Handle export button click
  const handleExportClick = () => {
    if (hasActiveFilters) {
      setExportDialogOpen(true)
    } else {
      performExport()
    }
  }

  const performExport = async () => {
    try {
      setExporting(true)
      const blob = await franchisePartnersService.export(filters)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `franchise_partners_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSuccess('Export downloaded successfully')
      setExportDialogOpen(false)
    } catch (err) {
      console.error('Export failed:', err)
      showError('Failed to export franchise partners')
    } finally {
      setExporting(false)
    }
  }

  const handleStatusUpdate = async (fpCode: string, newStatus: FPStatus) => {
    try {
      await franchisePartnersService.updateStatus(fpCode, newStatus)
      updatePartner(fpCode, { fp_status: newStatus })
      showSuccess(`Status updated: ${fpCode} is now '${newStatus}'`)
    } catch (error: any) {
      console.error('Failed to update status:', error)
      showError(`Failed to update status: ${error.response?.data?.detail || error.message}`)
      throw error
    }
  }

  const handleICUToggle = async (fpCode: string, isIcu: boolean) => {
    try {
      await franchisePartnersService.toggleICU(fpCode, isIcu)
      updatePartner(fpCode, { is_icu: isIcu })
      showSuccess(`ICU ${isIcu ? 'enabled' : 'disabled'} for ${fpCode}`)
    } catch (error: any) {
      console.error('Failed to toggle ICU:', error)
      showError(`Failed to toggle ICU: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleSellingToggle = async (fpCode: string, isSelling: boolean) => {
    try {
      await franchisePartnersService.toggleSelling(fpCode, isSelling)
      updatePartner(fpCode, { is_selling: isSelling })
      showSuccess(`Selling ${isSelling ? 'enabled' : 'disabled'} for ${fpCode}`)
    } catch (error: any) {
      console.error('Failed to toggle Selling:', error)
      showError(`Failed to toggle Selling: ${error.response?.data?.detail || error.message}`)
    }
  }

  const handleClearData = (fp?: any) => {
    if (fp) {
      setClearFp(fp.fp_code)
      setSelectedFranchisePartner(fp)
      setIsBulkOperation(false)
    } else {
      setClearFp('')
      setSelectedFranchisePartner(null)
      setIsBulkOperation(true)
    }
    setClearDataDialog(true)
  }

  const handleBulkSync = () => {
    setBulkSyncDialog(true)
  }

  const handleBulkSyncSuccess = (message: string) => {
    notifySyncQueued(message)
    setTimeout(() => {
      fetchActiveSyncs()
    }, 1000)
    setBulkSyncDialog(false)
    clearSelection()
  }

  const handleBulkSyncError = (message: string) => {
    showError(message)
    setBulkSyncDialog(false)
    clearSelection()
  }

  const handleClearDataSuccess = (message: string, triggerSyncFlag: boolean) => {
    if (triggerSyncFlag) {
      notifySyncQueued(message)
    } else {
      showSuccess(message)
    }
    setDataRefreshKey(prev => prev + 1)
    setTimeout(() => {
      refetch()
      fetchActiveSyncs()
    }, 1000)
    setClearDataDialog(false)
    setClearFp('')
    setSelectedFranchisePartner(null)
    if (isBulkOperation) {
      clearSelection()
    }
    setIsBulkOperation(false)
  }

  const handleClearDataError = (message: string) => {
    showError(message)
    if (isBulkOperation) {
      clearSelection()
    }
  }

  // Menu handlers
  const handleSyncMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, fp: any) => {
    setSyncMenuAnchor(prev => ({ ...prev, [fp.fp_code]: event.currentTarget }))
  }

  const handleSyncMenuClose = (fpCode: string) => {
    setSyncMenuAnchor(prev => ({ ...prev, [fpCode]: null }))
  }

  const handleQuickLinksMenuOpen = (event: React.MouseEvent<HTMLElement>, fp: FranchisePartner) => {
    setQuickLinksMenuAnchor(prev => ({ ...prev, [fp.fp_code]: event.currentTarget }))
  }

  const handleQuickLinksMenuClose = (fpCode: string) => {
    setQuickLinksMenuAnchor(prev => ({ ...prev, [fpCode]: null }))
  }

  const handleLinksMenuOpen = (event: React.MouseEvent<HTMLElement>, fp: FranchisePartner) => {
    setLinksMenuAnchor(prev => ({ ...prev, [fp.fp_code]: event.currentTarget }))
  }

  const handleLinksMenuClose = (fpCode: string) => {
    setLinksMenuAnchor(prev => ({ ...prev, [fpCode]: null }))
  }

  // Column definitions for BaseDataTable
  const columns: TableColumn<FranchisePartner>[] = [
    {
      key: 'checkbox',
      label: '',
      width: 50,
      renderHeader: () => (
        <Checkbox
          indeterminate={isIndeterminate(paginatedData)}
          checked={isAllSelected(paginatedData)}
          onChange={() => handleSelectAll(paginatedData)}
          sx={{
            color: '#fff',
            '&.Mui-checked': { color: '#fff' },
            '&.MuiCheckbox-indeterminate': { color: '#fff' }
          }}
        />
      ),
      render: (fp) => (
        <Checkbox
          checked={isSelected(fp.fp_code)}
          onChange={() => handleSelectFranchise(fp.fp_code)}
          sx={{
            color: poolwerxColors.teal,
            '&.Mui-checked': { color: poolwerxColors.teal }
          }}
        />
      )
    },
    {
      key: 'systems',
      label: 'Systems',
      render: (fp) => {
        const lightspeedUrl = formatters.getLightspeedUrl(fp)
        const xeroUrl = formatters.getXeroUrl(fp)
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {lightspeedUrl ? (
              <IconButton
                component="a"
                href={lightspeedUrl}
                target="_blank"
                size="small"
                sx={{ padding: '6px', position: 'relative' }}
              >
                <LightspeedIcon sx={{ opacity: fp.has_complete_lightspeed_connection ? 1 : 0.6 }} />
                {!fp.has_complete_lightspeed_connection && (
                  <WarningIcon sx={{ position: 'absolute', bottom: -2, right: -2, fontSize: 12, color: 'warning.main' }} />
                )}
              </IconButton>
            ) : (
              <IconButton size="small" disabled sx={{ padding: '6px', opacity: 0.3 }}>
                <LightspeedIcon sx={{ filter: 'grayscale(100%)' }} />
              </IconButton>
            )}
            {xeroUrl ? (
              <IconButton
                component="a"
                href={xeroUrl}
                target="_blank"
                size="small"
                sx={{ padding: '6px', position: 'relative' }}
              >
                <XeroIcon sx={{ opacity: fp.has_complete_xero_connection ? 1 : 0.6 }} />
                {!fp.has_complete_xero_connection && (
                  <WarningIcon sx={{ position: 'absolute', bottom: -2, right: -2, fontSize: 12, color: 'warning.main' }} />
                )}
              </IconButton>
            ) : (
              <IconButton size="small" disabled sx={{ padding: '6px', opacity: 0.3 }}>
                <XeroIcon sx={{ filter: 'grayscale(100%)' }} />
              </IconButton>
            )}
          </Box>
        )
      }
    },
    {
      key: 'franchise_name',
      label: 'Franchise Name',
      sortable: true,
      render: (fp) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={fp.country === 'AU' ? '\u{1F1E6}\u{1F1FA}' : fp.country === 'NZ' ? '\u{1F1F3}\u{1F1FF}' : fp.country === 'US' ? '\u{1F1FA}\u{1F1F8}' : '--'}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: fp.country === 'AU' ? '#E3F2FD' :
                              fp.country === 'NZ' ? '#F3E5F5' :
                              fp.country === 'US' ? '#FFEBEE' :
                              'transparent',
              borderColor: fp.country === 'AU' ? '#90CAF9' :
                          fp.country === 'NZ' ? '#E1BEE7' :
                          fp.country === 'US' ? '#FFCDD2' :
                          undefined,
              '& .MuiChip-label': { fontWeight: 500 }
            }}
          />
          <Link
            component={RouterLink}
            to={generateAdminFpUrl(fp.id, 'edit')}
            sx={{
              color: fp.fp_status === 'Terminated' ? 'text.secondary' : 'inherit',
              textDecoration: 'none',
              fontWeight: 800,
              '&:hover': { color: poolwerxColors.teal }
            }}
          >
            {fp.franchise_name}{fp.is_icu && ' \u{1F198}'}{fp.is_selling && ' \u{1F4B2}'}
          </Link>
          {fp.start_date && (
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontSize: '0.7rem' }}
            >
              {fp.start_date.slice(0, 7)}
            </Typography>
          )}
        </Box>
      )
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      align: 'center',
      render: (fp) => (
        <EditableStatusChip
          currentStatus={fp.fp_status as FPStatus}
          fpCode={fp.fp_code}
          onStatusUpdate={handleStatusUpdate}
        />
      )
    },
    {
      key: 'platform',
      label: 'Platform',
      align: 'center',
      render: (fp) => {
        if (fp.fp_status !== 'Terminated' && fp.system_status !== 'None') {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <Chip
                label={fp.system_status}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  ...(fp.system_status === 'None' && { bgcolor: '#e0e0e0', color: '#616161' }),
                  ...(fp.system_status === 'Provisioning' && { bgcolor: '#2196f3', color: '#fff' }),
                  ...(fp.system_status === 'Live' && { bgcolor: '#4caf50', color: '#fff' }),
                  ...(fp.system_status === 'Retiring' && { bgcolor: '#ff9800', color: '#fff' }),
                  ...(fp.system_status === 'Ended' && { bgcolor: '#757575', color: '#fff' })
                }}
              />
              <AdoptionIndicator status={fp.adoption_status} note={fp.adoption_note} />
            </Box>
          )
        }
        return (
          <Chip
            label={fp.system_status}
            size="small"
            sx={{ fontSize: '0.75rem', bgcolor: '#e0e0e0', color: '#616161' }}
          />
        )
      }
    },
    {
      key: 'manager_count',
      label: 'Users',
      align: 'center',
      sortable: true,
      render: (fp) => {
        const count = fp.manager_count || 0
        const accessTabUrl = generateAdminFpUrl(fp.id, 'edit#users')
        if (count === 0) {
          return (
            <Chip
              label="+"
              size="small"
              icon={<PersonAddIcon />}
              variant="outlined"
              component={RouterLink}
              to={accessTabUrl}
              clickable
              sx={{
                cursor: 'pointer',
                borderStyle: 'dashed',
                color: 'text.secondary',
                '& .MuiChip-icon': { opacity: 0.6 },
              }}
            />
          )
        }
        const loginAgo = fp.manager_latest_login ? formatTimeAgo(fp.manager_latest_login) : null
        const tooltipText = [
          fp.manager_names?.join(', '),
          loginAgo ? `Last login: ${loginAgo} ago` : 'Never logged in',
        ].filter(Boolean).join('\n')
        return (
          <Tooltip title={tooltipText}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={count}
                size="small"
                icon={<PeopleIcon />}
                component={RouterLink}
                to={accessTabUrl}
                clickable
                sx={{ cursor: 'pointer', bgcolor: '#4caf50', color: '#fff', '&:hover': { bgcolor: '#388e3c' }, '& .MuiChip-icon': { color: '#fff', pl: '3px' } }}
              />
              {loginAgo && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>
                  {loginAgo}
                </Typography>
              )}
            </Box>
          </Tooltip>
        )
      }
    },
    {
      key: 'data',
      label: 'Data Sync',
      align: 'center',
      render: (fp) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
          <SystemDataChips
            fp={fp}
            activeSyncs={activeSyncs}
            syncingFps={syncingFps}
            onTriggerSync={triggerSync}
          />
          <IconButton
            size="small"
            title={`Sync options - ${fp.franchise_name}`}
            onClick={(e) => handleSyncMenuOpen(e, fp)}
            sx={{ padding: '6px', color: 'inherit' }}
          >
            <span style={{ fontSize: '1.2rem' }}>{'\u{1F503}'}</span>
          </IconButton>
        </Box>
      )
    },
    {
      key: 'options',
      label: 'Options',
      align: 'center',
      render: (fp) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            component="a"
            href={formatters.getInspectorUrl(fp)}
            target="_blank"
            size="small"
            title={`Open Inspector - ${fp.franchise_name}`}
            sx={{
              padding: '8px',
              color: poolwerxColors.teal,
              '&:hover': { backgroundColor: `${poolwerxColors.teal}10` }
            }}
          >
            <InspectorIcon size={20} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleLinksMenuOpen(e, fp)}
            title={`Links - ${fp.franchise_name}`}
            sx={{
              padding: '8px',
              color: poolwerxColors.teal,
              '&:hover': { backgroundColor: `${poolwerxColors.teal}10` }
            }}
          >
            <DatasetLinkedIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => handleQuickLinksMenuOpen(e, fp)}
            title={`Quick links - ${fp.franchise_name}`}
            sx={{
              padding: '8px',
              color: poolwerxColors.teal,
              '&:hover': { backgroundColor: `${poolwerxColors.teal}10` }
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      )
    }
  ]

  // Row styling based on FP status
  const getRowSx = (fp: FranchisePartner) => ({
    ...(isSelected(fp.fp_code) && { bgcolor: 'action.selected' }),
    ...(fp.fp_status === 'Onboarding' && { bgcolor: '#e3f2fd' }),
    ...(fp.fp_status === 'Test' && { bgcolor: '#f3e5f5' }),
    ...(fp.fp_status === 'Terminated' && {
      bgcolor: 'grey.100',
      '& td': { color: 'text.secondary' }
    }),
    ...(fp.is_icu && {
      '& td': { color: '#d32f2f' }
    })
  })

  // Find which FP has an open menu (for rendering menus outside the table)
  const openSyncFpCode = Object.keys(syncMenuAnchor).find(k => syncMenuAnchor[k])
  const openSyncFp = openSyncFpCode ? paginatedData.find(fp => fp.fp_code === openSyncFpCode) : null

  const openQuickLinksFpCode = Object.keys(quickLinksMenuAnchor).find(k => quickLinksMenuAnchor[k])
  const openQuickLinksFp = openQuickLinksFpCode ? paginatedData.find(fp => fp.fp_code === openQuickLinksFpCode) : null

  const openLinksFpCode = Object.keys(linksMenuAnchor).find(k => linksMenuAnchor[k])
  const openLinksFp = openLinksFpCode ? paginatedData.find(fp => fp.fp_code === openLinksFpCode) : null

  return (
    <>
      <Box>
        <FeedbackNotificationBar />
        <AdminPageHeader
          pageKey="franchisePartners"
          countLabel={
            loading
              ? undefined
              : `${filteredData.length.toLocaleString()} ${filteredData.length === 1 ? 'partner' : 'partners'}`
          }
          actions={
            <Button
              component={RouterLink}
              to="/admin/franchise-partners/new"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: poolwerxColors.teal,
                '&:hover': {
                  bgcolor: poolwerxColors.teal,
                  opacity: 0.9
                }
              }}
            >
              Add Franchise Partner
            </Button>
          }
        />

        {/* Filter Card - Export lives here (beside Clear Filters), never in the header */}
        <ReportFilterCard
          filters={filters as unknown as ReportFilters}
          enabledFilters={ENABLED_FILTERS}
          onFilterChange={(key, value) => updateFilter((key === 'romFbc' ? 'bdm' : key) as keyof typeof filters, value)}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          loading={loading}
          exportButton={
            <Button
              variant="outlined"
              size="small"
              onClick={handleExportClick}
              startIcon={<DownloadIcon />}
              sx={{
                borderColor: '#E6E8EA',
                color: '#1F2A2E',
                px: 2,
                '&:hover': { borderColor: poolwerxColors.teal, backgroundColor: '#E6F4F5' }
              }}
            >
              Export Excel
            </Button>
          }
        />

        {/* Error State */}
        {dataError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {dataError}
          </Alert>
        )}

        {/* Bulk Actions Bar - Fixed at top */}
        {selectedFranchises.size > 0 && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: '280px',
              right: 0,
              zIndex: 1200,
              bgcolor: '#d0d0d0',
              borderBottom: '2px solid #9e9e9e',
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <Box sx={{ mx: 'auto', maxWidth: 'xl', px: 4, py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body1">
                  <strong>{selectedFranchises.size}</strong> franchise{selectedFranchises.size !== 1 ? 's' : ''} selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<MoreVertIcon />}
                    onClick={(e) => setBulkActionsMenuAnchor(e.currentTarget)}
                    sx={{
                      bgcolor: poolwerxColors.teal,
                      '&:hover': { bgcolor: poolwerxColors.teal, opacity: 0.9 }
                    }}
                  >
                    Bulk Actions
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => clearSelection()}
                    sx={{
                      borderColor: poolwerxColors.red,
                      color: poolwerxColors.red,
                      '&:hover': {
                        borderColor: poolwerxColors.redHover,
                        bgcolor: poolwerxColors.redLight
                      }
                    }}
                  >
                    Clear Selection
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Bulk Actions Menu */}
        <Menu
          anchorEl={bulkActionsMenuAnchor}
          open={Boolean(bulkActionsMenuAnchor)}
          onClose={() => setBulkActionsMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setBulkActionsMenuAnchor(null)
              handleBulkSync()
            }}
          >
            <ListItemIcon>
              <SyncAltIcon fontSize="small" sx={{ color: poolwerxColors.teal }} />
            </ListItemIcon>
            <ListItemText primary="Sync systems" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setBulkActionsMenuAnchor(null)
              handleClearData()
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText primary="Clear Data" />
          </MenuItem>
        </Menu>

        {/* Main Table */}
        <BaseDataTable<FranchisePartner>
          variant="enhanced"
          columns={columns}
          data={paginatedData}
          loading={loading}
          error={dataError}
          emptyMessage="No franchise partners found"
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={(field, direction) => handleSort(field as any)}
          getRowKey={(fp) => fp.id}
          getRowSx={getRowSx}
          countLabel="Franchise Partners"
          pagination={{
            page,
            perPage: rowsPerPage,
            total: filteredData.length,
            onPageChange: setPage,
            showFirstLast: true,
            size: 'large'
          }}
        />

        {/* Sync Menu (rendered outside table) */}
        {openSyncFp && (
          <Menu
            anchorEl={syncMenuAnchor[openSyncFp.fp_code]}
            open={Boolean(syncMenuAnchor[openSyncFp.fp_code])}
            onClose={() => handleSyncMenuClose(openSyncFp.fp_code)}
          >
            <MenuItem
              onClick={() => {
                triggerSyncAll(openSyncFp)
                handleSyncMenuClose(openSyncFp.fp_code)
              }}
              disabled={syncingFps[openSyncFp.fp_code] || !validators.canTriggerSync(openSyncFp, 'all')}
            >
              <ListItemIcon>
                <SyncAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sync all systems" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                triggerSync(openSyncFp, 'lightspeed')
                handleSyncMenuClose(openSyncFp.fp_code)
              }}
              disabled={syncingFps[openSyncFp.fp_code] || !openSyncFp.has_complete_lightspeed_connection}
            >
              <ListItemIcon>
                <LightspeedIcon sx={{ width: 16, height: 16 }} />
              </ListItemIcon>
              <ListItemText primary="Sync Lightspeed" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                triggerSync(openSyncFp, 'xero')
                handleSyncMenuClose(openSyncFp.fp_code)
              }}
              disabled={syncingFps[openSyncFp.fp_code] || !openSyncFp.has_complete_xero_connection}
            >
              <ListItemIcon>
                <XeroIcon sx={{ width: 16, height: 16 }} />
              </ListItemIcon>
              <ListItemText primary="Sync Xero" />
            </MenuItem>
            <Divider />
            <MenuItem
              component={RouterLink}
              to={generateAdminFpUrl(openSyncFp.id, 'connections')}
              onClick={() => handleSyncMenuClose(openSyncFp.fp_code)}
            >
              <ListItemIcon>
                <Typography component="span" sx={{ fontSize: '1rem' }}>{'\u{1F517}'}</Typography>
              </ListItemIcon>
              <ListItemText primary="Manage connections" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClearData(openSyncFp)
                handleSyncMenuClose(openSyncFp.fp_code)
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText>Clear Data</ListItemText>
            </MenuItem>
          </Menu>
        )}

        {/* Quick Links Menu (rendered outside table) */}
        {openQuickLinksFp && (
          <Menu
            anchorEl={quickLinksMenuAnchor[openQuickLinksFp.fp_code]}
            open={Boolean(quickLinksMenuAnchor[openQuickLinksFp.fp_code])}
            onClose={() => handleQuickLinksMenuClose(openQuickLinksFp.fp_code)}
          >
            <MenuItem
              component={RouterLink}
              to={generateAdminFpUrl(openQuickLinksFp.id, 'edit')}
              onClick={() => handleQuickLinksMenuClose(openQuickLinksFp.fp_code)}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Edit Franchise Partner" />
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to={generateAdminFpUrl(openQuickLinksFp.id, 'edit#users')}
              onClick={() => handleQuickLinksMenuClose(openQuickLinksFp.fp_code)}
            >
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Users list" />
            </MenuItem>
          </Menu>
        )}

        {/* External Links Menu (rendered outside table) */}
        {openLinksFp && (
          <Menu
            anchorEl={linksMenuAnchor[openLinksFp.fp_code]}
            open={Boolean(linksMenuAnchor[openLinksFp.fp_code])}
            onClose={() => handleLinksMenuClose(openLinksFp.fp_code)}
          >
            {[...(openLinksFp.resources ?? [])]
              .sort((a, b) => resourceTypeSortKey(a.resource_type) - resourceTypeSortKey(b.resource_type))
              .map(resource => {
              const url = resource.url
              const Icon = getResourceIcon(resource.resource_type)
              return (
                <MenuItem
                  key={resource.resource_code}
                  component={url ? 'a' : 'li'}
                  href={url || undefined}
                  target={url ? '_blank' : undefined}
                  rel={url ? 'noopener noreferrer' : undefined}
                  onClick={() => handleLinksMenuClose(openLinksFp.fp_code)}
                  disabled={!url}
                >
                  <ListItemIcon>
                    <Icon size={20} sx={{ opacity: url ? 1 : 0.4 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={resource.label}
                    secondary={!url ? 'Not configured' : undefined}
                    secondaryTypographyProps={{
                      sx: { fontSize: '0.75rem', color: 'text.secondary' }
                    }}
                  />
                </MenuItem>
              )
            })}
            <Divider />
            <MenuItem
              component={RouterLink}
              to={generateAdminFpUrl(openLinksFp.id, 'edit#resources')}
              onClick={() => handleLinksMenuClose(openLinksFp.fp_code)}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Update resources" />
            </MenuItem>
          </Menu>
        )}

        {/* Clear Data Dialog */}
        <ClearDataDialog
          open={clearDataDialog}
          onClose={() => {
            setClearDataDialog(false)
            setClearFp('')
            setSelectedFranchisePartner(null)
            setIsBulkOperation(false)
          }}
          franchisePartner={selectedFranchisePartner}
          franchisePartners={franchisePartners}
          isBulkOperation={isBulkOperation}
          selectedFranchises={selectedFranchises}
          onSuccess={handleClearDataSuccess}
          onError={handleClearDataError}
        />

        {/* Bulk Sync Dialog */}
        <BulkSyncDialog
          open={bulkSyncDialog}
          onClose={() => setBulkSyncDialog(false)}
          source="admin"
          selectedFpCodes={selectedFranchises}
          getFpDisplayName={(fpCode) =>
            franchisePartners.find((f) => f.fp_code === fpCode)?.franchise_name || fpCode
          }
          onSuccess={handleBulkSyncSuccess}
          onError={handleBulkSyncError}
        />

        {/* Export Confirm Dialog */}
        <ExportConfirmDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          onConfirm={performExport}
          activeFilters={getActiveFilterLabels()}
          recordCount={filteredData.length}
          exporting={exporting}
        />
      </Box>
    </>
  )
}

export default FranchisePartners
